/**
 * Feedback: a sheet where a parent tells Babia's makers about a problem, an
 * idea, a question, or something they like, and the button that opens it.
 * Picking a kind is optional (none picked is "general"). Closing the sheet
 * keeps the draft; it clears once the server has the message
 * (services/feedback.ts saves it and passes it to Sentry's feedback inbox).
 * While it holds a draft, Babia never reloads by itself for a new version.
 */
import { useCallback, useState } from "react";
import { track } from "../lib/analytics.ts";
import { useDraftHeld } from "../lib/app-update.tsx";
import { useSession } from "../lib/auth-client.ts";
import { type FeedbackKind, useSendFeedback } from "../lib/queries.ts";
import { cn } from "../lib/utils.ts";
import { Button } from "./ui/button.tsx";
import { Textarea } from "./ui/input.tsx";
import { Sheet } from "./ui/sheet.tsx";
import { useToast } from "./ui/toast.tsx";

/** The kinds a parent can pick; "general" is none of them. */
const KINDS: { kind: Exclude<FeedbackKind, "general">; emoji: string; label: string }[] = [
  { kind: "problem", emoji: "🐞", label: "Problem" },
  { kind: "idea", emoji: "💡", label: "Idea" },
  { kind: "question", emoji: "❓", label: "Question" },
  { kind: "praise", emoji: "💛", label: "Praise" },
];

const PLACEHOLDER: Record<FeedbackKind, string> = {
  general: "What’s on your mind?",
  problem: "What happened, and what did you expect?",
  idea: "What would make Babia better for you?",
  question: "What would you like to know?",
  praise: "What do you like about Babia?",
};

/** Where the sheet was opened, for analytics. */
export type FeedbackPlacement = "log" | "overview" | "settings";

export function FeedbackSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const send = useSendFeedback();
  const { data: session } = useSession();
  const [kind, setKind] = useState<FeedbackKind>("general");
  const [text, setText] = useState("");
  const message = text.trim();
  useDraftHeld(message !== "");

  function submit() {
    send.mutate(
      { kind, message },
      {
        onSuccess: () => {
          setKind("general");
          setText("");
          onClose();
          toast.show("Thank you! We read every message.");
        },
        onError: (err) => toast.show(`Couldn’t send — ${err.message}`),
      },
    );
  }

  return (
    <Sheet open={open} title="Feedback" emoji="💬" onClose={onClose}>
      <p className="mt-1 text-[14px] opacity-70">
        Something broken or confusing, an idea, a question? It goes straight to the people who make
        Babia.
      </p>
      <div role="group" aria-label="What it’s about" className="mt-3.5 grid grid-cols-4 gap-2">
        {KINDS.map((k) => (
          <Button
            key={k.kind}
            variant="chip"
            className={cn("flex-col gap-0.5 py-1.5 text-[14px]", kind === k.kind && "is-selected")}
            aria-pressed={kind === k.kind}
            onClick={() => setKind(kind === k.kind ? "general" : k.kind)}
          >
            <span aria-hidden="true" className="text-[18px] leading-none">
              {k.emoji}
            </span>
            {k.label}
          </Button>
        ))}
      </div>
      <Textarea
        className="mt-3"
        rows={4}
        maxLength={4000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={PLACEHOLDER[kind]}
        aria-label="Your message"
        autoFocus
      />
      <Button
        variant="primary"
        className="mt-3 w-full"
        disabled={!message || send.isPending}
        onClick={submit}
      >
        {send.isPending ? "Sending…" : "Send"}
      </Button>
      <p className="mt-2 text-[13px] opacity-65">
        Sent with your name and email{session ? ` (${session.user.email})` : ""}, so we can reply.
      </p>
    </Sheet>
  );
}

/** A button that opens the feedback sheet; the caller styles it. */
export function FeedbackButton({
  placement,
  className,
  children,
}: {
  placement: FeedbackPlacement;
  className?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          setOpen(true);
          track("feedback opened", { placement });
        }}
      >
        {children}
      </button>
      <FeedbackSheet open={open} onClose={close} />
    </>
  );
}
